/**
 * @file UserExperiments.tsx
 * @description This file contains the UserExperiments component,
 * which utilizes the ExperimentTable component to work with User Experiments data.
 */

import React from "react";
import { RouteComponentProps } from "@reach/router";
import "./ExperimentTable.scss";
import { UserExperiment } from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import ExperimentTable from "./ExperimentTable";
import { EMPTY_CELL_TEXT, formatExperimentsDate, MAX_SHOWN_GROUPED_USER_EMAILS } from "./utils";
import { OverlayTrigger } from "../Components";
import { Tooltip } from "react-bootstrap";
import * as uuid from "uuid";
import { GroupedDataRow } from "./types";

const userColumnRenderer = (data: GroupedDataRow<UserExperiment, "user_email">) => {
  if (!data.user_email) {
    return <div className="experimentCellText">{EMPTY_CELL_TEXT}</div>;
  }

  // If the number of user emails is less than or equal to the max, just show the names comma separated
  if (data.user_email.length <= MAX_SHOWN_GROUPED_USER_EMAILS) {
    return <div className="experimentCellText">{data.user_email.join(", ")}</div>;
  }

  // Otherwise, show "<emails> + <remaining> more" with an overlay on hover
  // Show all user emails in the overlay
  const sortedUserEmails = [...data.user_email].sort();
  const shownUserEmails = sortedUserEmails.slice(0, MAX_SHOWN_GROUPED_USER_EMAILS);
  const remainingCount = sortedUserEmails.length - shownUserEmails.length;

  return (
    <OverlayTrigger
      placement={OverlayTrigger.PLACEMENTS.BOTTOM.CENTER}
      overlay={
        <Tooltip id={uuid.v4()}>
          <div>
            {sortedUserEmails.map((userEmail, idx) => (
              <div key={idx}>{userEmail}</div>
            ))}
          </div>
        </Tooltip>
      }
    >
      <div className="experimentCellText">
        {`${shownUserEmails.join(", ")}${remainingCount > 0 ? ` + ${remainingCount} more` : ""}`}
      </div>
    </OverlayTrigger>
  );
};

const headers = [
  {
    label: "Experiment Flag",
    field: "experiment_flag",
    type: "select",
    options: "experiment_flag",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (experimentFlag: string) => {
      return !experimentFlag || experimentFlag.length === 0;
    },
  },
  {
    label: "Users With Access",
    field: "user_email",
    type: "select",
    options: "user_email",
    isMulti: true,
    flex: 7,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (userEmail: string) => {
      return !userEmail || userEmail.length === 0;
    },
    renderer: userColumnRenderer,
  },
  {
    label: "Last Modified",
    field: "lastmodified",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    renderer: (data: UserExperiment) => {
      if (!data.lastmodified) {
        return <div>{EMPTY_CELL_TEXT}</div>;
      }
      return <div>{formatExperimentsDate(data.lastmodified)}</div>;
    },
    uneditable: true,
  },
  {
    label: "Modified By",
    field: "lastuser",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    uneditable: true,
  },
];

const UserExperiments: React.FC<RouteComponentProps> = ({ path }) => {
  // Factory function to create new CompanyExperiment rows
  const createNewUserExperiment = (
    groupByColumn: string,
    groupedColumn: string
  ): UserExperiment => {
    const now = new Date().toISOString();
    return {
      experiment_flag: groupByColumn,
      user_email: groupedColumn,
      created: now,
      lastmodified: now,
    };
  };

  return (
    <ExperimentTable<UserExperiment>
      options={{
        headers,
        name: "User Experiments",
        addButtonText: "Add Experiment",
        createNewRow: createNewUserExperiment,
        groupByColumn: "experiment_flag",
        groupedColumn: "user_email",
        expectedKeys: ["experiment_flag", "user_email", "created", "lastmodified"],
        jsonField: "user_experiments",
        modalTitle: "New User Experiment",
      }}
    />
  );
};

export default UserExperiments;
